import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  FormControl,
  Select,
  MenuItem,
  Link,
  Typography,
} from '@mui/material';
import { Store as StoreIcon, MyLocation as MyLocationIcon, Check as CheckIcon } from '@mui/icons-material';
import { Business, BusinessFormData } from '../types';
import BusinessService from '../services/businessService';
import { useAuth } from '../contexts/AuthContext';
import { getEmailErrorMessage, getPhoneNumberErrorMessage } from '../utils/validation';
import { CHA } from './ChaaviVendorDashboardShell';

/** `#a87030` fill, white label; hover `CHA.accent` — matches vendor dashboard primary CTAs */
const chaaviGoldPrimaryButtonSx = {
  bgcolor: CHA.accentHover,
  color: '#fff',
  textTransform: 'none' as const,
  boxShadow: 'none',
  '&:hover': { bgcolor: CHA.accent, color: '#fff', boxShadow: 'none' },
};

/** Tokens aligned with edit_business_details_modal.html */
const EB = {
  ink: '#1a1a1a',
  sub: '#888888',
  label: '#999999',
  required: '#e24b4a',
  border: '#dddddd',
  borderSoft: '#e4e4e4',
  rule: '#f0f0f0',
  fieldBg: '#fafafa',
  green: '#4a7c59',
  greenHover: '#3d6b4b',
  greenMuted: '#f5f9f6',
  greenTint: '#f0f7f0',
  greenFocusRing: 'rgba(74,124,89,0.1)',
  hint: '#aaaaaa',
  cancelColor: '#666666',
} as const;

const inputPropsSx = {
  fontSize: 14,
  color: EB.ink,
  '&::placeholder': { color: '#aaa', opacity: 1 },
};

const outlinedFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    bgcolor: EB.fieldBg,
    minHeight: 40,
    transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
    '& fieldset': {
      borderWidth: '0.5px',
      borderColor: EB.border,
    },
    '&:hover fieldset': {
      borderColor: '#cccccc',
    },
    '&.Mui-focused': {
      bgcolor: '#ffffff',
      boxShadow: `0 0 0 3px ${EB.greenFocusRing}`,
    },
    '&.Mui-focused fieldset': {
      borderWidth: '0.5px',
      borderColor: EB.green,
    },
    '&.Mui-error fieldset': {
      borderColor: EB.required,
    },
  },
  '& .MuiOutlinedInput-input': {
    py: '10px',
    px: '12px',
    height: 'auto',
  },
  '& .MuiOutlinedInput-inputMultiline': {
    py: '10px',
    minHeight: 90,
    lineHeight: 1.5,
  },
};

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Typography
      component="label"
      sx={{
        fontSize: 11,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: EB.label,
        display: 'block',
        mb: '5px',
        '& .req': { color: EB.required, ml: '2px' },
      }}
    >
      {children}
      {required ? <Box component="span" className="req">*</Box> : null}
    </Typography>
  );
}

function SectionRule() {
  return <Box sx={{ height: '0.5px', bgcolor: EB.rule, my: 0.5 }} />;
}

interface BusinessManagementFormProps {
  open: boolean;
  onClose: () => void;
  business: Business | null;
  onSuccess: (business: Business) => void;
}

const BusinessManagementForm: React.FC<BusinessManagementFormProps> = ({
  open,
  onClose,
  business,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<BusinessFormData>({
    phoneNumber: '',
    businessName: '',
    businessDescription: '',
    businessCategory: '',
    businessAddress: '',
    latitude: undefined,
    longitude: undefined,
    businessPhone: '',
    businessEmail: '',
    website: '',
    socialMediaLinks: '',
    operatingHours: '',
  });
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [geocodingSuggestion, setGeocodingSuggestion] = useState<string | null>(null);
  const [businessEmailError, setBusinessEmailError] = useState<string>('');
  const [businessPhoneError, setBusinessPhoneError] = useState<string>('');

  const businessCategories = ['Tent_house', 'caters', 'hotel', 'farmhouse'];

  useEffect(() => {
    if (business) {
      setFormData({
        phoneNumber: business.phoneNumber,
        businessName: business.businessName,
        businessDescription: business.businessDescription,
        businessCategory: business.businessCategory,
        businessAddress: business.businessAddress,
        latitude: business.latitude,
        longitude: business.longitude,
        businessPhone: business.businessPhone,
        businessEmail: business.businessEmail,
        website: business.website || '',
        socialMediaLinks: business.socialMediaLinks || '',
        operatingHours: business.operatingHours || '',
      });
    } else {
      setFormData({
        phoneNumber: user?.phoneNumber || '',
        businessName: '',
        businessDescription: '',
        businessCategory: '',
        businessAddress: '',
        latitude: undefined,
        longitude: undefined,
        businessPhone: user?.phoneNumber || '',
        businessEmail: user?.email || '',
        website: '',
        socialMediaLinks: '',
        operatingHours: '',
      });
    }
    setError(null);
  }, [business, open, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'businessEmail') {
      setBusinessEmailError(getEmailErrorMessage(value));
    }

    if (name === 'businessPhone') {
      setBusinessPhoneError(getPhoneNumberErrorMessage(value));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGeocodeAddress = async () => {
    if (!formData.businessAddress || formData.businessAddress.trim() === '') {
      setGeocodingError('Please enter an address to geocode');
      setGeocodingSuggestion(null);
      return;
    }

    try {
      setGeocodingLoading(true);
      setGeocodingError(null);
      setGeocodingSuggestion(null);
      setError(null);
      const response = await BusinessService.geocodeAddress(formData.businessAddress);
      if (response && response.latitude && response.longitude) {
        setFormData((prev) => ({
          ...prev,
          latitude: response.latitude,
          longitude: response.longitude,
        }));
        setGeocodingError(null);
        setGeocodingSuggestion(null);
      } else {
        const errorMsg = response?.error || response?.message || 'Unable to geocode address.';
        const suggestion =
          response?.suggestion || 'You can manually enter latitude and longitude coordinates below.';
        setGeocodingError(errorMsg);
        setGeocodingSuggestion(suggestion);
      }
    } catch (err: any) {
      console.error('Error geocoding address:', err);
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to geocode address.';
      const suggestion =
        err?.response?.data?.suggestion ||
        'You can manually enter coordinates below, or find them using Google Maps.';
      setGeocodingError(errorMessage);
      setGeocodingSuggestion(suggestion);
    } finally {
      setGeocodingLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.businessName || !formData.businessDescription || !formData.businessCategory) {
      setError('Please fill in all required fields');
      return;
    }

    const emailErr = getEmailErrorMessage(formData.businessEmail);
    setBusinessEmailError(emailErr);
    if (emailErr) {
      setError(emailErr);
      return;
    }

    const phoneErr = getPhoneNumberErrorMessage(formData.businessPhone);
    setBusinessPhoneError(phoneErr);
    if (phoneErr) {
      setError(phoneErr);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let updatedBusiness: Business;

      if (business) {
        updatedBusiness = await BusinessService.updateBusiness(business.businessId, formData, user?.phoneNumber);
      } else {
        updatedBusiness = await BusinessService.createBusiness(formData);
      }

      onSuccess(updatedBusiness);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save business details');
      console.error('Error saving business:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const isEdit = Boolean(business);

  const FORM_ID = 'business-management-form';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(0,0,0,0.5)' },
      }}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 680,
          borderRadius: '16px',
          border: `0.5px solid ${EB.borderSoft}`,
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2, mb: 0, borderRadius: '8px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            px: 3.5,
            pt: 3,
            pb: 0,
            borderBottom: `0.5px solid ${EB.rule}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  bgcolor: CHA.accentHover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.1,
                }}
              >
                <StoreIcon sx={{ fontSize: 18, color: '#fff' }} />
              </Box>
              <Typography sx={{ fontSize: 17, fontWeight: 500, color: EB.ink, m: 0, mb: '3px' }}>
                {isEdit ? 'Edit business details' : 'Create business profile'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: EB.sub, m: 0, mb: 2.5 }}>
                {isEdit
                  ? 'Update your business profile visible to clients.'
                  : 'Add a new business profile visible to clients.'}
              </Typography>
            </Box>
            {isEdit && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: '#eaf3ec',
                  color: '#3d6b4b',
                  fontSize: 11,
                  fontWeight: 500,
                  px: 1,
                  py: 0.375,
                  borderRadius: '20px',
                  flexShrink: 0,
                  mt: 0.5,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: EB.green }} />
                Active
              </Box>
            )}
          </Box>
        </Box>

        <Box
          component="form"
          id={FORM_ID}
          onSubmit={handleSubmit}
          sx={{
            px: 3.5,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            <Box>
              <FieldLabel required>Business name</FieldLabel>
              <TextField
                required
                fullWidth
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                placeholder="Your business name"
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
            </Box>
            <Box>
              <FieldLabel required>Category</FieldLabel>
              <FormControl fullWidth required variant="outlined">
                <Select
                  name="businessCategory"
                  value={formData.businessCategory}
                  onChange={handleSelectChange}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Category' }}
                  renderValue={(v) => (v ? String(v) : '')}
                  sx={{
                    borderRadius: '8px',
                    bgcolor: EB.fieldBg,
                    minHeight: 40,
                    fontSize: 14,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '0.5px',
                      borderColor: EB.border,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cccccc' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '0.5px',
                      borderColor: EB.green,
                    },
                    '&.Mui-focused': {
                      bgcolor: '#fff',
                      boxShadow: `0 0 0 3px ${EB.greenFocusRing}`,
                    },
                  }}
                >
                  {businessCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box>
            <FieldLabel required>Description</FieldLabel>
            <TextField
              required
              fullWidth
              multiline
              rows={4}
              name="businessDescription"
              value={formData.businessDescription}
              onChange={handleInputChange}
              placeholder="Describe your business and services..."
              hiddenLabel
              variant="outlined"
              InputProps={{ sx: inputPropsSx }}
              sx={outlinedFieldSx}
            />
          </Box>

          <SectionRule />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' },
              gap: 1.5,
              alignItems: 'start',
            }}
          >
            <Box sx={{ gridColumn: { xs: '1', sm: '1 / 3' } }}>
              <FieldLabel required>Business address</FieldLabel>
              <TextField
                required
                fullWidth
                name="businessAddress"
                value={formData.businessAddress}
                onChange={handleInputChange}
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
            </Box>
            <Box sx={{ pt: { xs: 0, sm: '18px' }, justifySelf: { xs: 'stretch', sm: 'end' } }}>
              <Button
                type="button"
                variant="contained"
                onClick={handleGeocodeAddress}
                disabled={geocodingLoading || !formData.businessAddress}
                startIcon={
                  geocodingLoading ? (
                    <CircularProgress size={14} sx={{ color: '#fff' }} />
                  ) : (
                    <MyLocationIcon sx={{ fontSize: 14, color: '#fff' }} />
                  )
                }
                sx={{
                  height: '40px',
                  px: 2,
                  borderRadius: '8px',
                  fontSize: 13,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  ...chaaviGoldPrimaryButtonSx,
                }}
              >
                {geocodingLoading ? 'Getting…' : 'Get location'}
              </Button>
            </Box>
          </Box>

          {geocodingError && (
            <Alert severity="warning" sx={{ borderRadius: '8px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {geocodingError}
              </Typography>
              {geocodingSuggestion && (
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {geocodingSuggestion}
                </Typography>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Tip: You can get coordinates from{' '}
                  <Link href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.875rem' }}>
                    Google Maps
                  </Link>{' '}
                  by right-clicking on a location.
                </Typography>
              </Box>
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            <Box>
              <FieldLabel>Latitude</FieldLabel>
              <TextField
                fullWidth
                name="latitude"
                type="number"
                value={formData.latitude ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    latitude: e.target.value ? parseFloat(e.target.value) : undefined,
                  }))
                }
                placeholder="e.g. 28.6139"
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
              <Typography sx={{ fontSize: 11, color: EB.hint, mt: '1px' }}>Optional · for location filtering</Typography>
            </Box>
            <Box>
              <FieldLabel>Longitude</FieldLabel>
              <TextField
                fullWidth
                name="longitude"
                type="number"
                value={formData.longitude ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    longitude: e.target.value ? parseFloat(e.target.value) : undefined,
                  }))
                }
                placeholder="e.g. 77.2090"
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
              <Typography sx={{ fontSize: 11, color: EB.hint, mt: '1px' }}>Optional · for location filtering</Typography>
            </Box>
          </Box>

          <SectionRule />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            <Box>
              <FieldLabel required>Phone</FieldLabel>
              <TextField
                required
                fullWidth
                name="businessPhone"
                value={formData.businessPhone}
                onChange={handleInputChange}
                placeholder="+91 1234567890 or 1234567890"
                disabled={!business}
                error={!!businessPhoneError}
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
              {!business && (
                <Typography sx={{ fontSize: 11, color: EB.hint, mt: '1px' }}>Uses your account phone number</Typography>
              )}
              {businessPhoneError && (
                <Typography sx={{ fontSize: 11, color: EB.required, mt: '1px' }}>{businessPhoneError}</Typography>
              )}
            </Box>
            <Box>
              <FieldLabel required>Email</FieldLabel>
              <TextField
                required
                fullWidth
                name="businessEmail"
                type="email"
                value={formData.businessEmail}
                onChange={handleInputChange}
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
                error={!!businessEmailError}
              />
              {businessEmailError && (
                <Typography sx={{ fontSize: 11, color: EB.required, mt: '1px' }}>{businessEmailError}</Typography>
              )}
            </Box>
          </Box>

          <SectionRule />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            <Box>
              <FieldLabel>Website</FieldLabel>
              <TextField
                fullWidth
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://www.yourbusiness.com"
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
            </Box>
            <Box>
              <FieldLabel>Social media</FieldLabel>
              <TextField
                fullWidth
                name="socialMediaLinks"
                value={formData.socialMediaLinks}
                onChange={handleInputChange}
                placeholder="Instagram, Facebook, Twitter"
                hiddenLabel
                variant="outlined"
                InputProps={{ sx: inputPropsSx }}
                sx={outlinedFieldSx}
              />
            </Box>
          </Box>

          <Box>
            <FieldLabel>Operating hours</FieldLabel>
            <TextField
              fullWidth
              name="operatingHours"
              value={formData.operatingHours}
              onChange={handleInputChange}
              placeholder="e.g. Mon–Fri: 9AM–6PM, Sat: 10AM–4PM"
              hiddenLabel
              variant="outlined"
              InputProps={{ sx: inputPropsSx }}
              sx={outlinedFieldSx}
            />
          </Box>
        </Box>

        <Box
          sx={{
            px: 3.5,
            py: 2,
            pb: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            type="button"
            onClick={handleClose}
            disabled={loading}
            sx={{
              height: 38,
              px: 2.25,
              borderRadius: '8px',
              border: `0.5px solid ${EB.border}`,
              color: EB.cancelColor,
              textTransform: 'none',
              fontSize: 14,
              bgcolor: 'transparent',
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} sx={{ color: '#fff' }} />
              ) : (
                <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
              )
            }
            sx={{
              height: 38,
              px: 2.75,
              borderRadius: '8px',
              fontSize: 14,
              fontWeight: 500,
              '&:active': { transform: 'scale(0.98)' },
              ...chaaviGoldPrimaryButtonSx,
            }}
          >
            {loading ? 'Saving…' : isEdit ? 'Update business' : 'Create business'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessManagementForm;
